import React, { useEffect, useRef } from 'react';
import Glide from '@glidejs/glide';
import '@glidejs/glide/dist/css/glide.core.min.css';
import '@glidejs/glide/dist/css/glide.theme.min.css';
import {
    BuildrOnboardingIntroStep1,
    BuildrOnboardingIntroStep2,
    BuildrOnboardingIntroStep3,
    BuildrOnboardingIntroStep4,
} from '../../../assets';
import { ArrowRightBlack, ArrowRight } from '../../../assets';
import { BsChevronRight } from 'react-icons/bs';

interface OnboardingSlide {
    title: string;
    description: string;
    image: string;
    cta?: {
        action: 'close';
        label: string;
    };
}

const slides: OnboardingSlide[] = [
    {
        title: 'Welcome to Code.AI',
        description: 'The best coding curriculum in the world',
        image: BuildrOnboardingIntroStep1,
    },
    {
        title: '2-3X Depth & Practice',
        description: 'More practice than other programs',
        image: BuildrOnboardingIntroStep2,
    },
    {
        title: 'International Standards',
        description: 'Software coverage benchmarked to international standards',
        image: BuildrOnboardingIntroStep3,
    },
    {
        title: 'International Standards',
        description: 'Software coverage benchmarked to international standards',
        image: BuildrOnboardingIntroStep4,
        cta: {
            action: 'close',
            label: 'Experience Buildr',
        },
    },
];

const OnboardingCarousel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const glideRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function loadGlide() {
            if (glideRef.current) {
                new Glide(glideRef.current, {
                    type: 'slider',
                    perView: 1,
                    startAt: 0,
                    autoPlay: 5000,
                    gap: 0,
                    arrows: true,
                    bound: true,
                }).mount();
            }
        }

        let handler = setTimeout(() => {
            loadGlide();
        }, 500);

        return () => clearTimeout(handler);
    }, []);

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[250]">
            <div className="rounded-3xl p-0 w-[85%]">
                <div ref={glideRef} className="glide rounded-3xl">
                    <div
                        className="glide__track rounded-3xl"
                        data-glide-el="track"
                    >
                        <ul className="glide__slides">
                            {slides.map((slide, index) => (
                                <li
                                    key={index}
                                    className="glide__slide relative rounded-3xl"
                                >
                                    <img
                                        src={slide.image}
                                        alt={slide.title}
                                        className="w-full h-full object-contain rounded-3xl"
                                    />
                                    {slide.cta && (
                                        <button
                                            data-testid="onboarding-carouse-close-button"
                                            onClick={onClose}
                                            className="absolute bottom-6 right-6 bg-[#2d60e0] text-white px-4 py-3 rounded-3xl font-bold flex items-center"
                                        >
                                            {slide.cta.label}{' '}
                                            <BsChevronRight className="animate-bounceHorizontal h-[0.85rem]" />
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="glide__arrows" data-glide-el="controls">
                        <button
                            data-testid="onboarding-carouse-prev-arrow-button"
                            className="glide__arrow glide__arrow--left"
                            data-glide-dir="<"
                        >
                            <img
                                src={ArrowRightBlack}
                                alt="Previous"
                                className="w-6 h-6 rotate-180"
                            />
                        </button>
                        <button
                            data-testid="onboarding-carouse-next-arrow-button"
                            className="glide__arrow glide__arrow--right"
                            data-glide-dir=">"
                        >
                            <img
                                src={ArrowRight}
                                alt="Next"
                                className="w-6 h-6"
                            />
                        </button>
                    </div>
                    <div
                        className="glide__bullets"
                        data-glide-el="controls[nav]"
                    >
                        {slides.map((_, index) => (
                            <button
                                data-testid="onboarding-carouse-slide-button"
                                key={index}
                                className="glide__bullet"
                                data-glide-dir={`=${index}`}
                            ></button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OnboardingCarousel;
